import { DEFAULT_GUIDE_BASE_URL, getRuntimeConfig } from './runtime-config.js';
import { safeHttpUrl } from './safe-url.js';

/**
 * Links from the product into the employee guide on the public site.
 *
 * The guide is published at cti.customertimes.com in seven languages, one page per feature,
 * and every section of a page has a stable `#fragment`. Those fragments are the contract this
 * module depends on: a link points at a section, not at the top of a long article.
 *
 * English is what we link to, because the interface is English. The guide page carries its own
 * language switcher and keeps the route, so a reader who wants another language is one click
 * away from the same section.
 */

/** The base comes from the runtime config, so an environment can point these links at
    another copy of the site (a local preview, say) without a rebuild. It is operator-supplied
    and arrives over the wire, so it goes through the same check as every other configured link
    before it reaches an href — the feedback banner reads its URL exactly this way. Note that
    `safeHttpUrl` returns a normalized href, which puts back the trailing slash the runtime
    config stripped, so it comes off again here. */
function guideOrigin(): string {
  const configured = safeHttpUrl(getRuntimeConfig().guideBaseUrl).replace(
    /\/+$/,
    '',
  );
  return configured || DEFAULT_GUIDE_BASE_URL;
}

/**
 * A section of the skills guide. The values are the section ids on
 * `/en/guides/skills.html`; renaming one there renames it here.
 */
export type SkillsGuideSection =
  | 'where'
  | 'run'
  | 'agent-picks'
  | 'library'
  | 'company'
  | 'fork'
  | 'write'
  | 'from-chat'
  | 'files'
  | 'workspace'
  | 'import'
  | 'suggested';

export function skillsGuideUrl(section: SkillsGuideSection): string {
  return `${guideOrigin()}/en/guides/skills.html#${section}`;
}

/**
 * A section of the workspace-memory guide. The values are the section ids on
 * `/en/guides/workspaces-memory.html`; renaming one there renames it here.
 */
export type MemoryGuideSection =
  | 'what'
  | 'where'
  | 'map'
  | 'cards'
  | 'condition'
  | 'source'
  | 'suggestions'
  | 'history'
  | 'ask';

export function memoryGuideUrl(section: MemoryGuideSection): string {
  return `${guideOrigin()}/en/guides/workspaces-memory.html#${section}`;
}

/**
 * A section of the workspace callbacks guide. The values are the section ids on
 * `/en/guides/workspaces-callbacks.html`; renaming one there renames it here.
 */
export type CallbacksGuideSection = 'incoming' | 'outgoing' | 'ci-cd';

export function callbacksGuideUrl(section: CallbacksGuideSection): string {
  return `${guideOrigin()}/en/guides/workspaces-callbacks.html#${section}`;
}

/**
 * A section of the Quality project variables guide. The values are the section ids on
 * `/en/guides/digital-qa-project-variables.html`; renaming one there renames it here.
 */
export type QualityVariablesGuideSection = 'overview';

export function qualityVariablesGuideUrl(
  section: QualityVariablesGuideSection,
): string {
  return `${guideOrigin()}/en/guides/digital-qa-project-variables.html#${section}`;
}

/**
 * A Quality guide article on the public site. The values are the page slugs under `/en/guides/`;
 * renaming a page there renames it here.
 */
export type QualityGuideSlug =
  | 'generate-test-cases'
  | 'prepare-test-generation-sources'
  | 'digital-qa-first-run-setup'
  | 'digital-qa-make-existing-cases-runnable'
  | 'digital-qa-after-a-run'
  | 'digital-qa-restart-or-rerun'
  | 'digital-qa-point-a-run-at-the-app'
  | 'digital-qa-project-variables'
  | 'digital-qa-sign-in-credentials'
  | 'digital-qa-salesforce-jwt-sign-in'
  | 'digital-qa-human-help'
  | 'digital-qa-mobile-first-run'
  | 'digital-qa-test-data-and-files'
  | 'digital-qa-caching-and-replay'
  | 'knowledge-to-test-cases'
  | 'workspaces-connectors-and-credentials'
  | 'workspaces-quality-and-developer';

/** A section of a Quality guide article: `/en/guides/<slug>.html#<section>`. */
export function qualityGuideUrl(
  slug: QualityGuideSlug,
  section: string,
): string {
  return `${guideOrigin()}/en/guides/${slug}.html#${encodeURIComponent(section)}`;
}

/** One entry of the CT Agent guide, addressed by its stable entry id. */
export function agentGuideEntryUrl(id: string): string {
  return `${guideOrigin()}/en/solutions/agent-guide.html#${encodeURIComponent(id)}`;
}
